import type { StandbyEntryStatus } from '@/api/standby/standbyPublicEntryQuery';

export type StandbyEntryUiStateInput = {
  status: StandbyEntryStatus | string | null | undefined;
  notifyExpiresAt?: string | null;
};

// `waiting`/`offered`/`declined`/`expired` render in place; every other kind redirects to the flow start.
export type StandbyEntryUiState =
  | { kind: 'waiting' }
  | { kind: 'left' }
  | { kind: 'booked' }
  | { kind: 'expired' }
  | { kind: 'unavailable' }
  // SQR-14768 — the shop closed while the client was still waiting. Only
  // `getStandbyStatusPageState` returns this; the base mapper never does.
  | { kind: 'dayEnded' }
  // A `notified` entry whose offer deadline is still ahead.
  | { kind: 'offered' }
  // Client-only transient — never returned here; the screen sets it after a decline.
  | { kind: 'declined' };

export type StandbyEntryUiStateKind = StandbyEntryUiState['kind'];

// `notified` past notifyExpiresAt (or missing it) is a clock-skew safety net into `expired`, same as `timed_out`.
export const getStandbyEntryUiState = (
  entry: StandbyEntryUiStateInput | null | undefined,
): StandbyEntryUiState => {
  switch (entry?.status) {
    case 'joined':
      return { kind: 'waiting' };
    case 'notified': {
      const { notifyExpiresAt } = entry;
      const isLive =
        !!notifyExpiresAt && new Date(notifyExpiresAt).getTime() > Date.now();
      return isLive ? { kind: 'offered' } : { kind: 'expired' };
    }
    case 'timed_out':
    case 'expired':
      return { kind: 'expired' };
    case 'removed':
      return { kind: 'left' };
    case 'booked':
      return { kind: 'booked' };
    default:
      return { kind: 'unavailable' };
  }
};

/**
 * Status-page overlay (SQR-14768): a client still waiting (`joined`/`timed_out`)
 * past the shop's close of business sees the "No openings today" state. Kept
 * separate from `getStandbyEntryUiState` so the drawer and the redirect effect
 * keep the single-arg mapper unchanged. A `notified` offer never becomes
 * `dayEnded`; an unparseable close time degrades to the underlying state
 * (waiting), never a crash.
 */
export const getStandbyStatusPageState = ({
  status,
  closeOfBusinessAt,
  notifyExpiresAt,
  now = Date.now(),
}: {
  status: StandbyEntryStatus | string | null | undefined;
  closeOfBusinessAt?: string | null;
  notifyExpiresAt?: string | null;
  now?: number;
}): StandbyEntryUiState => {
  if (
    (status === 'joined' || status === 'timed_out') &&
    closeOfBusinessAt &&
    Date.parse(closeOfBusinessAt) <= now
  ) {
    return { kind: 'dayEnded' };
  }
  return getStandbyEntryUiState({ status, notifyExpiresAt });
};
